/* eslint-disable react-refresh/only-export-components --
   This module exports the data router (createBrowserRouter), not a component,
   so its internal shell components can't hot-refresh independently — edits
   here trigger a full reload in dev, which is the standard trade-off for the
   router-module pattern. */
import React, { lazy, Suspense, useEffect, useLayoutEffect, useRef, Component } from 'react';
import type { ReactNode } from 'react';
import { createBrowserRouter, Navigate, Outlet, useLocation } from 'react-router-dom';
import { isJoLunoHost } from '@/utils/siteHosts';

import NotFoundPage from '@/components/NotFoundPage';
import { MaintenanceGate } from '@/components/MaintenanceGate';
import {
  toolLoaders, warmRoute, homeLoader,
  imageShareLoader, fileShareLoader, formsLoader, schedulerLoader,
  photographyLoader, blogPostLoader, redirectLoader,
} from '@/routeLoaders';
import { getPageTitle } from '@/data/moduleLibrary';
import { consumeScrollToTopSuppression } from '@/utils/navigation';
import { logEvent } from '@/utils/log';
import { DoopeyMark } from '@/components/DoopeyMark';
import styles from './App.module.css';

class ChunkErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error: Error) {
    const isChunkError = /loading chunk|dynamically imported module|failed to fetch/i.test(error.message);
    const reloadKey = 'chunk_reload_ts';
    const lastReload = Number(sessionStorage.getItem(reloadKey) || '0');
    const now = Date.now();
    if (isChunkError && now - lastReload > 10000) {
      sessionStorage.setItem(reloadKey, String(now));
      // Use location.replace to force a full document fetch, bypassing any
      // browser cache for the current URL. This guarantees the fresh index.html
      // (now served with Cache-Control: no-cache) is fetched on deploy.
      window.location.replace(window.location.href);
    }
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className={styles.errorScreen}>
          <div className={styles.errorCard}>
            <strong>Page failed to load</strong>
            <p>The app could not fetch this route's code. Reloading usually clears a stale deploy chunk.</p>
          </div>
          <button type="button" className={styles.reloadButton} onClick={() => window.location.reload()}>
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Track which paths have been logged this session to avoid duplicate events
const loggedPaths = new Set<string>();
// Only a tab's first page view says where the visitor came from.
let landed = false;

/** The admin screens aren't page views. Jo's other visits count like anyone's. */
function isAdminScreen(pathname: string): boolean {
  const path = pathname.toLowerCase();
  return path === '/admin' || path.startsWith('/admin/');
}

/** The host of the page that sent the visitor here, or '' when there's none. */
function referrerHost(): string {
  try {
    return document.referrer ? new URL(document.referrer).host : '';
  } catch {
    return '';
  }
}

/* Reset scroll to top when the path changes (SPA keeps window scroll otherwise). Hash-only updates do not reset. Library-back uses consumeScrollToTopSuppression so scrollIntoView is not overwritten.
   A layout effect, so the reset lands before the new page paints, and before
   the page's own layout effects (Home opens on its tools that way). */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const prevPathnameRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    const prev = prevPathnameRef.current;
    if (prev === null) {
      prevPathnameRef.current = pathname;
      return;
    }
    if (prev !== pathname) {
      prevPathnameRef.current = pathname;
      if (consumeScrollToTopSuppression()) return;
      if (!hash) window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  return null;
}

/* Set document.title and log page views on route change */
function SetPageTitle() {
  const { pathname } = useLocation();
  useEffect(() => {
    // joluno.joleu.ng serves the Jo Luno routes at clean paths — map them back
    // to their canonical module paths for title lookup.
    const titlePath = isJoLunoHost()
      ? (pathname === '/' ? '/joluno' : pathname === '/links' ? '/joluno/links' : pathname)
      : pathname;
    document.title = getPageTitle(titlePath);
    if (!loggedPaths.has(pathname)) {
      loggedPaths.add(pathname);
      const first = !landed;
      landed = true;
      // One page.viewed per page per tab is the view Admin's analytics count.
      if (!isAdminScreen(pathname)) {
        const page = pathname === '/' ? 'home' : pathname.slice(1);
        logEvent('page.viewed', first ? { page, referrer: referrerHost() } : { page });
      }
    }
  }, [pathname]);
  return null;
}
// Warm the landing route's chunk right away — in parallel with React booting —
// instead of after the first render discovers which <Route> matched.
warmRoute(window.location.pathname);

const Home = lazy(homeLoader);

const ImageShareLazy = lazy(imageShareLoader);
const FileShareLazy = lazy(fileShareLoader);
const FormsLazy = lazy(formsLoader);
const SchedulerLazy = lazy(schedulerLoader);
const RedirectLazy = lazy(redirectLoader);

/* ── Tool routes: [path, lazyComponent] ────────────────────────────────
   Built from the loader table in routeLoaders.ts (single source of truth,
   shared with warmRoute for chunk prefetching). Routes that share one
   component instance across paths keep that shared component here.
   ──────────────────────────────────────────────────────────────────── */
const sharedLazy: Record<string, React.LazyExoticComponent<React.ComponentType>> = {
  '/imageshare':     ImageShareLazy,
  '/fileshare':      FileShareLazy,
  '/forms':          FormsLazy,
  '/scheduler':      SchedulerLazy,
  '/redirect':       RedirectLazy,
};
const toolRoutes: [string, React.LazyExoticComponent<React.ComponentType>][] =
  toolLoaders.map(([path, load]) => [path, sharedLazy[path] ?? lazy(load)]);

const Photography      = lazy(photographyLoader);
const BlogPostContent  = lazy(blogPostLoader);
const AdminLayout      = lazy(() => import('./tools/admin/AdminLayout'));
const Admin            = lazy(() => import('./tools/admin/Admin'));
const AdminLogs        = lazy(() => import('./tools/admin/AdminLogs'));
const AdminEmail       = lazy(() => import('./tools/admin/AdminEmail'));
const AdminTemplates   = lazy(() => import('./tools/admin/AdminTemplates'));
const AdminPhotography = lazy(() => import('./tools/admin/AdminPhotography'));
const AdminAnalytics   = lazy(() => import('./tools/admin/AdminAnalytics'));
const AdminFlags       = lazy(() => import('./tools/admin/AdminFlags'));
const AdminRedirects   = lazy(() => import('./tools/admin/AdminRedirects'));
const AdminShares      = lazy(() => import('./tools/admin/AdminShares'));
const AdminBookings    = lazy(() => import('./tools/admin/AdminBookings'));

function Loading() {
  return (
    <div className={styles.loadingScreen} aria-live="polite" aria-busy="true">
      <div className={styles.loadingCard}>
        <div className={styles.loadingMark} aria-hidden><DoopeyMark decorative /></div>
        <div className={styles.loadingCopy}>
          <span>Loading Doopey's World</span>
          <div className={styles.loadingBars} aria-hidden>
            <i />
            <i />
            <i />
          </div>
        </div>
      </div>
    </div>
  );
}

function MaybeMaintenanceGate({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  if (pathname.startsWith('/admin')) return children;
  return <MaintenanceGate>{children}</MaintenanceGate>;
}

/* Shared shell for every route: chunk-error recovery, maintenance gate,
   suspense fallback, scroll reset, and titles — identical wrapper stack to
   the old <App>, with <Outlet /> where <Routes> used to be. Because
   ChunkErrorBoundary and Suspense sit INSIDE the layout route, chunk load
   failures and lazy suspension are still handled by our components, not the
   router's default error UI. */
function RootLayout() {
  return (
    <ChunkErrorBoundary>
    <MaybeMaintenanceGate>
    <Suspense fallback={<Loading />}>
      <ScrollToTop />
      <SetPageTitle />
      <Outlet />
    </Suspense>
    </MaybeMaintenanceGate>
    </ChunkErrorBoundary>
  );
}

/* joluno.joleu.ng serves this same build as Jo Luno's standalone home with
   clean URLs: / is the landing, /links the streaming links. Legacy module
   paths redirect so old links and pre-clean-URL rewrites keep working. */
const JoLunoLandingPage = lazy(() => import('./tools/joluno-landing/JoLunoLanding'));
const JoLunoLinksPage = lazy(() => import('./tools/joluno/JoLuno'));

const joLunoRoutes = [
  {
    element: <RootLayout />,
    children: [
      { path: '/', element: <JoLunoLandingPage /> },
      { path: '/links', element: <JoLunoLinksPage /> },
      { path: '/joluno', element: <Navigate to="/" replace /> },
      { path: '/joluno/links', element: <Navigate to="/links" replace /> },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
];

/* Data router (route objects instead of descendant <Routes>): Link's
   viewTransition option only reaches the router from data routes — inside
   the old declarative tree it was silently dropped. Route elements stay
   plain React.lazy components; the router only owns URL matching. */
export const router = createBrowserRouter(isJoLunoHost() ? joLunoRoutes : [
  {
    element: <RootLayout />,
    children: [
      { path: '/', element: <Home /> },

      ...toolRoutes.map(([path, Comp]) => ({ path, element: <Comp /> })),

      // Routes with params, redirects, and admin
      { path: '/photography', element: <Photography /> },
      { path: '/photography/:album', element: <Photography /> },
      { path: '/i/:id', element: <ImageShareLazy /> },
      { path: '/f/:id', element: <FileShareLazy /> },
      { path: '/t/:id', element: <FormsLazy /> },
      { path: '/scheduler/:id', element: <SchedulerLazy /> },
      { path: '/redirect/:code', element: <RedirectLazy /> },
      { path: '/blog/:slug', element: <BlogPostContent /> },
      // /joluno-landing is 301'd at the worker level

      // One admin lock and the bar between the screens, kept across them.
      {
        path: '/admin',
        element: <AdminLayout />,
        children: [
          { index: true, element: <Admin /> },
          { path: 'logs', element: <AdminLogs /> },
          { path: 'email', element: <AdminEmail /> },
          { path: 'templates', element: <AdminTemplates /> },
          { path: 'photography', element: <AdminPhotography /> },
          { path: 'analytics', element: <AdminAnalytics /> },
          { path: 'flags', element: <AdminFlags /> },
          { path: 'redirects', element: <AdminRedirects /> },
          { path: 'shares', element: <AdminShares /> },
          { path: 'bookings', element: <AdminBookings /> },
        ],
      },

      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
